import React, { useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { useGetTPPColumnVersionsQuery } from '../queries/useGetTPPColumnVersionsQuery'
import VersionEntry from './VersionEntry'
import VersionPreviewModal from './VersionPreviewModal'
import { useTPPChangeLogModalContext } from '../TPPChangeLogModalProvider'

interface VersionListProps {
    columnId: string
    tppId: string
}

const VersionList = ({ columnId, tppId }: VersionListProps) => {
    const { data, isLoading, isError } = useGetTPPColumnVersionsQuery(columnId)
    const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
    const { handleStartCompare } = useTPPChangeLogModalContext()

    if (isLoading) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress size={40} />
            </Stack>
        )
    }

    if (isError) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" minHeight={200}>
                <Typography variant="16x400" color="error">
                    Error loading version history. Please try again.
                </Typography>
            </Stack>
        )
    }

    if (!data?.versions || data.versions.length === 0) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" minHeight={200}>
                <Typography variant="16x400" color="figma.grayscale.textHier2">
                    No versions saved yet.
                </Typography>
            </Stack>
        )
    }

    return (
        <Box
            sx={{
                position: 'relative',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'auto',
                    pr: 1,
                }}
            >
                <Stack>
                    {data.versions.map((version) => (
                        <VersionEntry
                            key={version._id}
                            version={version}
                            onOpen={() => setSelectedVersionId(version._id)}
                            onCompare={() =>
                                handleStartCompare({
                                    versionId: version._id,
                                    columnId,
                                    columnTitle: version.columnTitle,
                                    versionNumber: version.versionNumber,
                                })
                            }
                        />
                    ))}
                </Stack>
                <VersionPreviewModal
                    versionId={selectedVersionId}
                    tppId={tppId}
                    onClose={() => setSelectedVersionId(null)}
                />
            </Box>
        </Box>
    )
}

export default VersionList
